import React, { useEffect, type FC } from "react";

import qs from "qs";
import { useLocation } from "react-router-dom";
import { useRecoilCallback } from "recoil";

import { evalCondition } from "mailup-react-helpers";
import { Flex, ToastContainer, Tooltip } from "mailup-web-components";

import Page from "Containers/Page";
import BoxFilters from "Pages/RecipientsList/components/BoxFilters";
import BoxResults from "Pages/RecipientsList/components/BoxResults";
import Header from "Pages/RecipientsList/components/Header";
import PanelEditRecipient from "Pages/RecipientsList/components/aside/PanelEditRecipient";
import PanelConfigColumns from "Pages/RecipientsList/components/columns/PanelConfigColumns";
import PanelEditContacts from "Pages/RecipientsList/components/contacts/PanelEditContacts";
import PanelAddRecipient from "Pages/RecipientsList/components/insert/PanelAddRecipient";
import ModalAddSegment from "Pages/RecipientsList/components/modals/ModalAddSegment";
import ModalBulkAddToGroup from "Pages/RecipientsList/components/modals/ModalBulkAddToGroup";
import ModalBulkFindReplace from "Pages/RecipientsList/components/modals/ModalBulkFindReplace";
import ModalBulkRemoveFromGroups from "Pages/RecipientsList/components/modals/ModalBulkRemoveFromGroups";
import ModalBulkUnsubscribe from "Pages/RecipientsList/components/modals/ModalBulkUnsubscribe";
import ModalConfigColumns from "Pages/RecipientsList/components/modals/ModalConfigColumns";
import ModalDelStatsContact from "Pages/RecipientsList/components/modals/ModalDelStatsContact";
import ModalDelete from "Pages/RecipientsList/components/modals/ModalDelete";
import ModalFilterDetails from "Pages/RecipientsList/components/modals/ModalFilterDetails";
import ModalPending from "Pages/RecipientsList/components/modals/ModalPending";
import ModalRecipientGroups from "Pages/RecipientsList/components/modals/ModalRecipientGroups";
import ModalRecipientTracking from "Pages/RecipientsList/components/modals/ModalRecipientTracking";
import ModalSaveSegment from "Pages/RecipientsList/components/modals/ModalSaveSegment";
import ModalSendConfirmation from "Pages/RecipientsList/components/modals/ModalSendConfirmation";
import ModalSubscribe from "Pages/RecipientsList/components/modals/ModalSubscribe";
import ModalUnsubscribe from "Pages/RecipientsList/components/modals/ModalUnsubscribe";
import EngagementTooltip from "Pages/RecipientsList/components/results/EngagementTooltip";

import DataSyncPromo from "Widgets/DataSyncPromo/DataSyncPromo";

import { isNullOrEmpty } from "Helpers/strings";

import { isBaseConfiguration } from "Helpers/options";
import usePanelsCoordinator from "Pages/RecipientsList/hooks/usePanelsCoordinator";
import type { PageParams } from "Pages/RecipientsList/model";
import { panelAddRecipientOpenCallback } from "Pages/RecipientsList/recoil/callbacks/addRecipient";
import {
  initPageCallback,
  resetPageCallback,
} from "Pages/RecipientsList/recoil/callbacks/callbacks";
import { panelEditRecipientOpenCallback } from "Pages/RecipientsList/recoil/callbacks/recipient";
import BoxDeliverabilityIndex from "./components/BoxDeliverabilityIndex";
import { gainsightTrack } from "mailup-react-helpers";

const RecipientsList: FC = () => {
  const location = useLocation();
  const qsParams: PageParams = qs.parse(location.search.replace(/^\?/, ""));

  const initPage = useRecoilCallback(initPageCallback, []);
  const resetPage = useRecoilCallback(resetPageCallback, []);

  const openRecipient = useRecoilCallback(panelEditRecipientOpenCallback, []);
  const addRecipient = useRecoilCallback(panelAddRecipientOpenCallback, []);

  const canEdit: boolean = evalCondition("uo:EDIT");
  const isOfferLight = isBaseConfiguration();

  const init = async (): Promise<void> => {
    await initPage(qsParams);

    if (!isNullOrEmpty(qsParams.rid) && qsParams.rid !== undefined && canEdit) {
      void openRecipient(Number.parseInt(qsParams.rid, 10));
    } else if (qsParams.nr !== undefined) {
      void addRecipient();
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    gainsightTrack("mup_recipients_visit_recipientsList", {});

    void init();

    return (): void => {
      void resetPage();
    };
  }, []);

  usePanelsCoordinator();

  return (
    <>
      <Header />
      <Tooltip />
      <ToastContainer />
      <ModalUnsubscribe />
      <ModalDelete />
      <ModalConfigColumns />
      <ModalFilterDetails />

      <ModalBulkUnsubscribe />
      <ModalBulkAddToGroup />
      <ModalBulkRemoveFromGroups />

      <ModalBulkFindReplace />

      <ModalSaveSegment />

      <ModalSubscribe />
      <ModalPending />
      <ModalSendConfirmation />
      <ModalRecipientGroups />

      <ModalDelStatsContact />

      <ModalRecipientTracking />

      <ModalAddSegment />

      <PanelEditRecipient />
      <PanelAddRecipient />
      <PanelEditContacts />
      <PanelConfigColumns />

      <EngagementTooltip />
      <Page onReload={init}>
        <Flex flexDirection="column" gap="16px">
          <BoxDeliverabilityIndex />
          <BoxFilters />
          <BoxResults />

          {!isOfferLight && <DataSyncPromo />}
        </Flex>
      </Page>
    </>
  );
};

export default RecipientsList;
